import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarDays, Plus, Users, Clock, AlertTriangle, Award, GraduationCap, Flame, Wrench } from 'lucide-react';
import { format } from 'date-fns';
import { projectAPI } from '../services/api';
import { SKILLS } from '../utils/skills';

// Paliers posés par Samuel le 2026-09-27 (voir backend/geocoding.py) : 1A (0-5 km) à
// 5 (40-50 km), 6 = au-delà, pour ne laisser aucun chantier sans zone.
const ZONES_TRAJET = ['1A', '1B', '2', '3', '4', '5', '6'];

const EnhancedProjectForm = ({ onCreateProject, editingProject, onUpdateProject, isOpen, onOpenChange, employees = [], initialData = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    clientName: '',
    clientAddress: '',
    travelZoneOverride: '',
    quoteAmountHT: '',
    quoteAmountTTC: '',
    purchasesAmountHT: '',
    actualHoursWorked: '',
    hoursToSell: '',
    minEmployees: '',
    startMode: 'free', // 'free' | 'month' | 'precise'
    startDate: '',
    startMonth: '',
    isComplex: false,
    deadline: '',
    isUrgent: false,
    requiredSkills: [],
    manualAssignment: false,
    assignedEmployees: [],
    siteManagerId: ''
  });
  const [calcStatus, setCalcStatus] = useState(null); // { type: 'success'|'error', message } | null
  const [isCalculating, setIsCalculating] = useState(false);
  // Factures Pennylane sans devis : lues sur le serveur à l'ouverture (le rattachement automatique a pu
  // se faire depuis le chargement de la page). linkOverride : null tant que rien n'est changé ici ;
  // ainsi un enregistrement sans toucher aux factures n'écrase jamais un rattachement fait entre-temps.
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [linkOverride, setLinkOverride] = useState(null);
  React.useEffect(() => {
    setLinkOverride(null);
    if (!isOpen || !editingProject) return;
    projectAPI.availableInvoices().then(setAvailableInvoices).catch(() => setAvailableInvoices([]));
  }, [isOpen, editingProject]);
  const currentLinks = linkOverride
    ?? availableInvoices.filter((i) => editingProject && i.linkedProjectId === editingProject.id).map((i) => i.id);
  const invoiceById = Object.fromEntries(availableInvoices.map((i) => [i.id, i]));
  const eur2 = (v) => `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const shortLabel = (i) => `${i.number} · ${(i.label || '').replace(/^Facture\s+/i, '').replace(/\s*-\s*\S+$/, '')} · ${eur2(i.amountHT)} · ${i.date}`;

  React.useEffect(() => {
    if (editingProject) {
      setFormData({
        name: editingProject.name || '',
        clientName: editingProject.clientName || '',
        clientAddress: editingProject.clientAddress || '',
        travelZoneOverride: editingProject.travelZoneOverride || '',
        quoteAmountHT: editingProject.quoteAmountHT?.toString() || '',
        quoteAmountTTC: editingProject.quoteAmountTTC?.toString() || '',
        purchasesAmountHT: editingProject.purchasesAmountHT?.toString() || '',
        invoicedAmountHT: editingProject.invoicedAmountHT?.toString() || '',
        plannedHoursOverride: editingProject.plannedHoursOverride?.toString() || '',
        actualHoursWorked: editingProject.actualHoursWorked?.toString() || '',
        hoursToSell: editingProject.hoursToSell?.toString() || '',
        minEmployees: editingProject.minEmployees?.toString() || '',
        startMode: editingProject.hasFixedStartDate
          ? 'precise'
          : (editingProject.startMonth ? 'month' : 'free'),
        startDate: editingProject.startDate || '',
        startMonth: editingProject.startMonth || '',
        isComplex: editingProject.isComplex || false,
        deadline: editingProject.deadline || '',
        isUrgent: editingProject.isUrgent || false,
        requiredSkills: editingProject.requiredSkills || [],
        // En édition, l'équipe est toujours modifiable explicitement
        manualAssignment: true,
        assignedEmployees: editingProject.assignedEmployees || [],
        siteManagerId: editingProject.siteManagerId || ''
      });
    } else if (initialData) {
      // Pré-remplissage depuis un devis Pennylane importé : le client/adresse/
      // montants/heures estimées sont suggérés, mais équipe/durée/date restent
      // à choisir normalement (comme pour toute création de chantier).
      setFormData({
        name: initialData.quoteNumber ? `${initialData.clientName || 'Chantier'} — ${initialData.quoteNumber}` : (initialData.clientName || ''),
        clientName: initialData.clientName || '',
        clientAddress: initialData.clientAddress || '',
        travelZoneOverride: '',
        quoteAmountHT: initialData.quoteAmountHT?.toString() || '',
        quoteAmountTTC: initialData.quoteAmountTTC?.toString() || '',
        purchasesAmountHT: '',
        actualHoursWorked: '',
        hoursToSell: initialData.hoursToSell?.toString() || '',
        minEmployees: '',
        startMode: 'free',
        startDate: '',
        startMonth: '',
        isComplex: false,
        deadline: '',
        isUrgent: false,
        requiredSkills: [],
        manualAssignment: false,
        assignedEmployees: [],
        siteManagerId: ''
      });
    } else {
      // Reset form for new project
      setFormData({
        name: '',
        clientName: '',
        clientAddress: '',
        travelZoneOverride: '',
        quoteAmountHT: '',
        quoteAmountTTC: '',
        purchasesAmountHT: '',
        actualHoursWorked: '',
        hoursToSell: '',
        minEmployees: '',
        startMode: 'free',
        startDate: '',
        startMonth: '',
        isComplex: false,
        deadline: '',
        isUrgent: false,
        requiredSkills: [],
        manualAssignment: false,
        assignedEmployees: [],
        siteManagerId: ''
      });
    }
  }, [editingProject, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.hoursToSell || !formData.minEmployees) return;
    if (formData.startMode === 'precise' && !formData.startDate) return;
    if (formData.startMode === 'month' && !formData.startMonth) return;

    const hasFixedStartDate = formData.startMode === 'precise';
    const startDateValue = hasFixedStartDate ? formData.startDate : null;
    const startMonthValue = formData.startMode === 'month' ? formData.startMonth : null;

    // Validation pour chantier complexe avec date imposée
    if (formData.isComplex && hasFixedStartDate) {
      const confirmed = window.confirm(
        'Attention: Un chantier complexe avec date imposée peut nécessiter une réorganisation du planning. Continuer ?'
      );
      if (!confirmed) return;
    }

    if (editingProject) {
      // Mode édition : l'équipe assignée est toujours explicite et modifiable
      const updatedProject = {
        ...editingProject,
        name: formData.name,
        clientName: formData.clientName || null,
        clientAddress: formData.clientAddress || null,
        travelZoneOverride: formData.travelZoneOverride || null,
        quoteAmountHT: formData.quoteAmountHT ? parseFloat(formData.quoteAmountHT) : null,
        quoteAmountTTC: formData.quoteAmountTTC ? parseFloat(formData.quoteAmountTTC) : null,
        purchasesAmountHT: formData.purchasesAmountHT ? parseFloat(formData.purchasesAmountHT) : null,
        actualHoursWorked: formData.actualHoursWorked ? parseFloat(formData.actualHoursWorked) : null,
        invoicedAmountHT: formData.invoicedAmountHT ? parseFloat(formData.invoicedAmountHT) : null,
        plannedHoursOverride: formData.plannedHoursOverride ? parseFloat(formData.plannedHoursOverride) : null,
        hoursToSell: parseInt(formData.hoursToSell),
        minEmployees: parseInt(formData.minEmployees),
        hasFixedStartDate,
        startDate: startDateValue,
        startMonth: startMonthValue,
        // Une date fixe (cochée ou déjà en place) doit se refléter sur la
        // date RÉELLE du chantier, sinon le moteur continue de le positionner
        // sur son ancien calcul automatique — la case "date fixe" pouvait être
        // cochée sans aucun effet visible sur le planning. Un chantier déjà
        // flexible n'est pas touché ici : sa position actuelle (héritée du
        // spread ci-dessus) reste intacte tant qu'on ne fixe pas de date.
        ...(hasFixedStartDate ? { actualStartDate: startDateValue } : {}),
        isComplex: formData.isComplex,
        deadline: formData.deadline || null,
        isUrgent: formData.isUrgent,
        requiredSkills: formData.requiredSkills,
        assignedEmployees: formData.assignedEmployees,
        siteManagerId: formData.siteManagerId || null
      };
      // Rattachements de factures : envoyés seulement s'ils ont été changés ici (voir linkOverride)
      if (linkOverride !== null) {
        updatedProject.linkedInvoiceIds = linkOverride;
        updatedProject.invoiceLinksManual = true;
      } else {
        delete updatedProject.linkedInvoiceIds;
        delete updatedProject.invoiceLinksManual;
      }
      onUpdateProject(updatedProject);
    } else {
      // Mode création : auto-assignation par défaut, ou sélection manuelle si activée
      const newProject = {
        name: formData.name,
        clientName: formData.clientName || null,
        clientAddress: formData.clientAddress || null,
        travelZoneOverride: formData.travelZoneOverride || null,
        quoteAmountHT: formData.quoteAmountHT ? parseFloat(formData.quoteAmountHT) : null,
        quoteAmountTTC: formData.quoteAmountTTC ? parseFloat(formData.quoteAmountTTC) : null,
        purchasesAmountHT: formData.purchasesAmountHT ? parseFloat(formData.purchasesAmountHT) : null,
        hoursToSell: parseInt(formData.hoursToSell),
        minEmployees: parseInt(formData.minEmployees),
        duration: Math.ceil(parseInt(formData.hoursToSell) / (parseInt(formData.minEmployees) * 35)), // Estimation
        hasFixedStartDate,
        startDate: startDateValue,
        startMonth: startMonthValue,
        actualStartDate: hasFixedStartDate ? formData.startDate : null,
        isComplex: formData.isComplex,
        deadline: formData.deadline || null,
        isUrgent: formData.isUrgent,
        requiredSkills: formData.requiredSkills,
        assignedEmployees: formData.manualAssignment ? formData.assignedEmployees : [],
        siteManagerId: formData.siteManagerId || null,
        status: 'planned',
        pennylaneQuoteId: initialData?.id || null
      };
      onCreateProject(newProject);
    }

    // Reset form
    setFormData({
      name: '',
      clientName: '',
      clientAddress: '',
      travelZoneOverride: '',
      quoteAmountHT: '',
      quoteAmountTTC: '',
      purchasesAmountHT: '',
      actualHoursWorked: '',
      hoursToSell: '',
      minEmployees: '',
      startMode: 'free',
      startDate: '',
      startMonth: '',
      isComplex: false,
      deadline: '',
      isUrgent: false,
      requiredSkills: [],
      manualAssignment: false,
      assignedEmployees: [],
      siteManagerId: ''
    });
    setCalcStatus(null);
    onOpenChange(false);
  };

  const toggleRequiredSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      requiredSkills: prev.requiredSkills.includes(skill)
        ? prev.requiredSkills.filter(s => s !== skill)
        : [...prev.requiredSkills, skill]
    }));
  };

  const toggleAssignedEmployee = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      assignedEmployees: prev.assignedEmployees.includes(employeeId)
        ? prev.assignedEmployees.filter(id => id !== employeeId)
        : [...prev.assignedEmployees, employeeId]
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCalculateHours = async () => {
    if (!formData.quoteAmountHT) return;
    setIsCalculating(true);
    setCalcStatus(null);
    try {
      const result = await projectAPI.calculateHoursFromQuote(
        parseFloat(formData.quoteAmountHT),
        formData.purchasesAmountHT ? parseFloat(formData.purchasesAmountHT) : 0
      );
      if (result.success) {
        setFormData(prev => ({ ...prev, hoursToSell: result.hoursToSell.toString() }));
        setCalcStatus({
          type: 'success',
          message: `${result.hoursToSell}h (main d'œuvre ${result.laborAmountHT}€ HT ÷ ${result.laborSellPrice}€/h)`
        });
      } else {
        setCalcStatus({ type: 'error', message: result.reason });
      }
    } catch (error) {
      setCalcStatus({ type: 'error', message: "Impossible de contacter le calcul (backend indisponible)" });
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {!editingProject && (
        <DialogTrigger asChild>
          <Button className="bg-green-500 hover:bg-green-600 text-white transition-all duration-200 shadow-lg hover:shadow-xl">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Chantier
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-2xl bg-white border border-green-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center text-green-800">
            <CalendarDays className="w-5 h-5 mr-2" />
            {editingProject ? 'Modifier le chantier' : 'Créer un nouveau chantier'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-green-700">Nom du chantier</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Ex: Rénovation Maison Dupont"
              className="border-green-200 focus:border-green-400 focus:ring-green-200"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="clientName" className="text-green-700">Client</Label>
              <Input
                id="clientName"
                value={formData.clientName}
                onChange={(e) => handleInputChange('clientName', e.target.value)}
                placeholder="Ex: M. et Mme Dupont"
                className="border-green-200 focus:border-green-400 focus:ring-green-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clientAddress" className="text-green-700">Adresse du chantier</Label>
              <Input
                id="clientAddress"
                value={formData.clientAddress}
                onChange={(e) => handleInputChange('clientAddress', e.target.value)}
                placeholder="Ex: 12 rue des Lilas, 75000 Paris"
                className="border-green-200 focus:border-green-400 focus:ring-green-200"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-green-700">Zone de trajet (prime)</Label>
            <div className="flex items-center gap-2">
              <Select
                value={formData.travelZoneOverride || 'auto'}
                onValueChange={(v) => handleInputChange('travelZoneOverride', v === 'auto' ? '' : v)}
              >
                <SelectTrigger className="border-green-200 w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatique (par distance)</SelectItem>
                  {ZONES_TRAJET.map((z) => (
                    <SelectItem key={z} value={z}>Zone {z} (forcée)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!formData.travelZoneOverride && (
                editingProject?.travelZoneAuto ? (
                  <span className="text-xs text-gray-500">
                    Calculé : {editingProject.travelDistanceKm} km → Zone {editingProject.travelZoneAuto}
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">
                    {editingProject ? 'Pas encore calculé (adresse société à renseigner dans Paramètres, ou adresse chantier introuvable)' : 'Calculé à l\'enregistrement'}
                  </span>
                )
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quoteAmountHT" className="text-green-700">Montant devis HT (€)</Label>
              <Input
                id="quoteAmountHT"
                type="number"
                step="0.01"
                value={formData.quoteAmountHT}
                onChange={(e) => handleInputChange('quoteAmountHT', e.target.value)}
                onBlur={handleCalculateHours}
                placeholder="Ex: 8500"
                className="border-green-200 focus:border-green-400 focus:ring-green-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quoteAmountTTC" className="text-green-700">Montant devis TTC (€)</Label>
              <Input
                id="quoteAmountTTC"
                type="number"
                step="0.01"
                value={formData.quoteAmountTTC}
                onChange={(e) => handleInputChange('quoteAmountTTC', e.target.value)}
                placeholder="Ex: 10200"
                className="border-green-200 focus:border-green-400 focus:ring-green-200"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="purchasesAmountHT" className="text-green-700">Achats HT (€)</Label>
            <Input
              id="purchasesAmountHT"
              type="number"
              step="0.01"
              value={formData.purchasesAmountHT}
              onChange={(e) => handleInputChange('purchasesAmountHT', e.target.value)}
              onBlur={handleCalculateHours}
              placeholder="Ex: 2000"
              className="border-green-200 focus:border-green-400 focus:ring-green-200 max-w-[240px]"
            />
            <p className="text-xs text-green-600">
              Matériel, sous-traitance... Sert à déduire les heures prévues ci-dessous.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hours" className="text-green-700 flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                Heures vendues
                {isCalculating && <span className="ml-2 text-xs text-green-500 animate-pulse">calcul…</span>}
              </Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="hours"
                  type="number"
                  value={formData.hoursToSell}
                  onChange={(e) => handleInputChange('hoursToSell', e.target.value)}
                  placeholder="280"
                  className="border-green-200 focus:border-green-400 focus:ring-green-200"
                  required
                />
              </div>
              {calcStatus && (
                <p className={`text-xs ${calcStatus.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {calcStatus.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="employees" className="text-green-700 flex items-center">
                <Users className="w-4 h-4 mr-1" />
                Min. salariés
              </Label>
              <Input
                id="employees"
                type="number"
                value={formData.minEmployees}
                onChange={(e) => handleInputChange('minEmployees', e.target.value)}
                placeholder="2"
                className="border-green-200 focus:border-green-400 focus:ring-green-200"
                required
              />
            </div>
          </div>

          {editingProject && (editingProject.status === 'in-progress' || editingProject.status === 'completed') && (
            <div className="space-y-2">
              <Label htmlFor="actualHoursWorked" className="text-green-700">Heures réelles pondérées</Label>
              <Input
                id="actualHoursWorked"
                type="number"
                step="0.5"
                value={formData.actualHoursWorked}
                onChange={(e) => handleInputChange('actualHoursWorked', e.target.value)}
                placeholder={`Ex: ${formData.hoursToSell || '280'}`}
                className="border-green-200 focus:border-green-400 focus:ring-green-200 max-w-[240px]"
              />
              <p className="text-xs text-green-600">
                Heures réellement effectuées à ce jour — recalculées depuis les pointages.
              </p>
            </div>
          )}

          {editingProject && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invoicedAmountHT" className="text-green-700">Montant facturé HT (€)</Label>
                <Input
                  id="invoicedAmountHT"
                  type="number"
                  step="0.01"
                  value={formData.invoicedAmountHT ?? ''}
                  onChange={(e) => handleInputChange('invoicedAmountHT', e.target.value)}
                  placeholder={editingProject.pennylaneQuoteId ? 'Repris de Pennylane' : 'Ex: 8500'}
                  className="border-green-200 focus:border-green-400 focus:ring-green-200"
                />
                <p className="text-xs text-green-600">
                  {editingProject.pennylaneQuoteId
                    ? 'Repris des factures Pennylane du devis ; ce montant ne sert que si aucune n’est émise.'
                    : 'Chantier sans devis Pennylane : à saisir une fois facturé.'}
                  {' '}Compte dans la marge une fois le chantier terminé.
                </p>
                {!editingProject.pennylaneQuoteId && (
                  <div className="space-y-1.5 pt-1">
                    <div className="text-xs font-medium text-green-700">Factures Pennylane rattachées</div>
                    {currentLinks.length === 0 && (
                      <p className="text-xs text-gray-500">Aucune : la marge se calcule sur le devis (ou le montant saisi ci-dessus).</p>
                    )}
                    {currentLinks.map((id) => (
                      <div key={id} className="flex items-center justify-between gap-2 text-xs bg-green-50 border border-green-200 rounded px-2 py-1">
                        <span>{invoiceById[id] ? shortLabel(invoiceById[id]) : id}</span>
                        <button type="button" className="text-red-500 hover:text-red-700 shrink-0"
                                onClick={() => setLinkOverride(currentLinks.filter((x) => x !== id))}>
                          Détacher
                        </button>
                      </div>
                    ))}
                    <select value="" onChange={(e) => e.target.value && setLinkOverride([...currentLinks, e.target.value])}
                            className="w-full border border-green-200 rounded px-2 py-1 text-xs bg-white">
                      <option value="">Rattacher une facture…</option>
                      {availableInvoices.filter((i) => !i.linkedProjectId).map((i) => (
                        <option key={i.id} value={i.id}>{shortLabel(i)}</option>
                      ))}
                    </select>
                    <p className="text-xs text-green-600">
                      Rattachées automatiquement quand le client, le montant et la date concordent sans ambiguïté ;
                      sinon à faire ici. Leur total remplace le devis dans la marge, une fois le chantier terminé.
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="plannedHoursOverride" className="text-green-700">Heures prévues imposées</Label>
                <Input
                  id="plannedHoursOverride"
                  type="number"
                  step="0.5"
                  value={formData.plannedHoursOverride ?? ''}
                  onChange={(e) => handleInputChange('plannedHoursOverride', e.target.value)}
                  placeholder="Calcul du devis"
                  className="border-green-200 focus:border-green-400 focus:ring-green-200"
                />
                <p className="text-xs text-green-600">
                  Heures pondérées. Vide : (devis HT − achats HT) ÷ prix de vente main-d'œuvre.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="deadline" className="text-green-700">À terminer avant (optionnel)</Label>
              <Input
                id="deadline"
                type="date"
                value={formData.deadline}
                onChange={(e) => handleInputChange('deadline', e.target.value)}
                className="border-green-200 focus:border-green-400 focus:ring-green-200"
              />
              <p className="text-xs text-green-600">
                Le moteur place ce chantier en priorité et alerte si la date devient intenable.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-green-700">Priorité</Label>
              <div className="flex items-start space-x-2 pt-1">
                <Checkbox
                  id="isUrgent"
                  checked={formData.isUrgent}
                  onCheckedChange={(checked) => handleInputChange('isUrgent', checked)}
                  className="border-red-300"
                />
                <Label htmlFor="isUrgent" className="text-red-700 cursor-pointer flex items-center">
                  <Flame className="w-4 h-4 mr-1 text-red-500" />
                  Urgent (passe devant tout)
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-green-700 flex items-center">
              <Wrench className="w-4 h-4 mr-1" />
              Spécialités requises (optionnel)
            </Label>
            <div className="flex flex-wrap gap-3">
              {SKILLS.map(skill => (
                <label key={skill} htmlFor={`skill-${skill}`} className="flex items-center space-x-1.5 cursor-pointer">
                  <Checkbox
                    id={`skill-${skill}`}
                    checked={formData.requiredSkills.includes(skill)}
                    onCheckedChange={() => toggleRequiredSkill(skill)}
                    className="border-green-300"
                  />
                  <span className="text-sm text-green-800">{skill}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-green-600">
              L'équipe assignée devra couvrir ensemble toutes les spécialités cochées.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="complex"
                checked={formData.isComplex}
                onCheckedChange={(checked) => handleInputChange('isComplex', checked)}
                className="border-orange-300"
              />
              <div className="space-y-1">
                <Label htmlFor="complex" className="text-green-700 cursor-pointer flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-1 text-orange-500" />
                  Chantier complexe
                </Label>
                <p className="text-xs text-orange-600">
                  Nécessite une attention particulière lors de l'assignation
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="siteManagerId" className="text-green-700 flex items-center">
                <Award className="w-4 h-4 mr-1" />
                Responsable du chantier
              </Label>
              <select
                id="siteManagerId"
                value={formData.siteManagerId}
                onChange={(e) => handleInputChange('siteManagerId', e.target.value)}
                className="w-full px-3 py-2 border border-green-200 rounded-md focus:ring-2 focus:ring-green-200 focus:border-green-400"
              >
                <option value="">Choisi automatiquement (ouvrier qualifié)</option>
                {employees
                  .filter(emp => emp.isQualified && (emp.active !== false || emp.id === formData.siteManagerId))
                  .map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.fullName}{emp.active === false ? ' (inactif)' : ''}
                    </option>
                  ))}
              </select>
              <p className="text-xs text-green-600">
                Épinglé dans l'équipe, il mène le chantier de bout en bout et ne change pas
                d'une réorganisation à l'autre. Seuls les ouvriers qualifiés peuvent l'être.
              </p>
            </div>

            {!editingProject && (
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="manualAssignment"
                  checked={formData.manualAssignment}
                  onCheckedChange={(checked) => handleInputChange('manualAssignment', checked)}
                  className="border-green-300"
                />
                <div className="space-y-1">
                  <Label htmlFor="manualAssignment" className="text-green-700 cursor-pointer">
                    Assignation manuelle des salariés
                  </Label>
                  <p className="text-xs text-green-600">
                    Sinon, l'équipe est assignée automatiquement selon les disponibilités
                  </p>
                </div>
              </div>
            )}

            {(editingProject || formData.manualAssignment) && (
              <div className="space-y-2">
                <Label className="text-green-700 flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  Salariés assignés ({formData.assignedEmployees.length})
                </Label>
                <div className="border border-green-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-green-100">
                  {employees.length === 0 && (
                    <p className="text-xs text-green-600 italic p-3">Aucun salarié disponible</p>
                  )}
                  {employees.filter(emp => emp.active !== false || formData.assignedEmployees.includes(emp.id)).map(employee => (
                    <label
                      key={employee.id}
                      htmlFor={`assign-${employee.id}`}
                      className="flex items-center space-x-2 p-2 cursor-pointer hover:bg-green-50"
                    >
                      <Checkbox
                        id={`assign-${employee.id}`}
                        checked={formData.assignedEmployees.includes(employee.id)}
                        onCheckedChange={() => toggleAssignedEmployee(employee.id)}
                        className="border-green-300"
                      />
                      <span className="text-sm text-green-800 flex-1">{employee.fullName}</span>
                      {employee.isQualified && <Award className="w-3.5 h-3.5 text-blue-500" />}
                      {employee.isApprentice && <GraduationCap className="w-3.5 h-3.5 text-yellow-500" />}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {formData.hoursToSell && formData.minEmployees && (
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="space-y-1 text-sm">
                <p className="text-green-700">
                  <strong>Durée estimée :</strong> {Math.ceil(parseInt(formData.hoursToSell) / (parseInt(formData.minEmployees) * 35))} semaine(s)
                </p>
                <p className="text-xs text-green-600">
                  Basé sur 35h/semaine par employé (estimation standard)
                </p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-green-700">Démarrage du chantier</Label>
            <RadioGroup
              value={formData.startMode}
              onValueChange={(value) => handleInputChange('startMode', value)}
              className="space-y-3"
            >
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="free" id="startModeFree" className="border-green-300 text-green-600" />
                  <Label htmlFor="startModeFree" className="text-green-700 cursor-pointer font-normal">
                    Date libre
                  </Label>
                </div>
                <p className="text-xs text-green-600 pl-6">
                  Intégration automatique à la suite du planning existant
                </p>
              </div>

              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="month" id="startModeMonth" className="border-green-300 text-green-600" />
                  <Label htmlFor="startModeMonth" className="text-green-700 cursor-pointer font-normal">
                    Mois de démarrage souhaité
                  </Label>
                </div>
                {formData.startMode === 'month' && (
                  <Input
                    id="startMonth"
                    type="month"
                    value={formData.startMonth}
                    onChange={(e) => handleInputChange('startMonth', e.target.value)}
                    className="ml-6 max-w-[180px] border-green-200 focus:border-green-400 focus:ring-green-200"
                    required
                  />
                )}
              </div>

              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="precise" id="startModePrecise" className="border-green-300 text-green-600" />
                  <Label htmlFor="startModePrecise" className="text-green-700 cursor-pointer font-normal">
                    Date précise imposée
                  </Label>
                </div>
                {formData.startMode === 'precise' && (
                  <Input
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    className="ml-6 max-w-[180px] border-green-200 focus:border-green-400 focus:ring-green-200"
                    required
                  />
                )}
              </div>
            </RadioGroup>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-green-200 text-green-700 hover:bg-green-50"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="bg-green-500 hover:bg-green-600 text-white transition-all duration-200"
            >
              {editingProject ? 'Mettre à jour' : 'Créer le chantier'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedProjectForm;